// screens/Calendar.tsx — B17a: the agenda. Grouped by day, next thing
// first; a cancelled event is shown STRUCK WITH THE WORD (never colour or
// strike alone) rather than hidden — a meeting that fell through is
// something a morning needs to know.
//
// Deliberately an agenda, not a month grid (spec §2: the grid is later
// polish IF daily use asks for it). Times: `startLocal` + `timezone` come
// from the server, rendered as the event's OWN local time — the client
// never re-derives locality from the absolute instant (the B6a lesson).
//
// ⚑ THE PERSONAL TAB MOUNTS THIS SAME SCREEN (build 1 of the 01B nav,
// 2026-10-08). Everything it adds is optional and off by default, so the
// standalone `#/calendar` renders exactly what it always did:
//   * `family` — a second tenant read BESIDE this one (ruling N3, never a
//     switch). Its events join the agenda tinted and labelled; `showMine` /
//     `showFamily` are the toggles the parent draws. A family read that
//     fails is said in a named line, never an empty agenda.
//   * `onEditAuthored` — an authored event opens its edit sheet; an invite
//     stays read-only (it belongs to its organizer).
//   * `header` / `footer` — the parent's toggles and its reminders, inside
//     this screen's own scroll region so nothing is clipped at the fold.
import type { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import {
  ApiError,
  listAgenda,
  type FamilyTenantApi,
  type HoldApi,
  type CalendarEventApi,
} from '../lib/api';
import './Calendar.css';

export interface CalendarProps {
  tenantId: string;
  onOpen: (id: string) => void;
  /** Inside the Personal tab: the sub-tab already says "Calendar". */
  embedded?: boolean;
  /** The family tenant read beside this one, when the viewer is in it. */
  family?: FamilyTenantApi | null;
  showMine?: boolean;
  showFamily?: boolean;
  /** Bump to read again after a write. */
  reloadKey?: number;
  /** userId -> name, for "added by". */
  names?: Record<string, string>;
  meId?: string | null;
  onEditAuthored?: (event: CalendarEventApi, tenantId: string) => void;
  header?: ComponentChildren;
  footer?: ComponentChildren;
}

type Origin = 'mine' | 'family';
type AgendaEvent = CalendarEventApi & { origin: Origin };

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; events: AgendaEvent[]; holds: HoldApi[]; familyError: string | null };

const WINDOW_DAYS = 60;

/** The grouping key is a real, sortable local date (`2026-12-30`) — the
 * human label is derived from it at render time, so a window that crosses
 * a year boundary still orders December before January. */
function dayKey(iso: string): string {
  const d = new Date(iso);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

function dayLabel(key: string): string {
  return new Date(`${key}T12:00:00`).toLocaleDateString([], {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

function timeLabel(e: CalendarEventApi): string {
  // The server-rendered local wall time when the event carries one; the
  // viewer's local rendering otherwise.
  if (e.startLocal) {
    const t = e.startLocal.slice(11, 16);
    return e.timezone ? `${t} ${e.timezone.split('/').pop()?.replace('_', ' ')}` : t;
  }
  return new Date(e.startsAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

const REPEAT_WORD: Record<string, string> = {
  DAILY: 'repeats daily',
  WEEKLY: 'repeats weekly',
  MONTHLY: 'repeats monthly',
  YEARLY: 'repeats yearly',
};

/** "repeats weekly" from an RRULE, or null. The agenda reads each event's
 * first occurrence only (`GET /api/events`), so a repeating event SAYS it
 * repeats rather than leaving the later days looking free. */
function repeatWord(rrule: string | null | undefined): string | null {
  if (!rrule) return null;
  const freq = /FREQ=([A-Z]+)/.exec(rrule)?.[1];
  return freq ? (REPEAT_WORD[freq] ?? 'repeats') : 'repeats';
}

/** "Sep 12" — the far end of a span, said once on the day it begins. */
function throughLabel(iso: string): string {
  return new Date(`${iso}T12:00:00`).toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export function Calendar({
  tenantId,
  onOpen,
  embedded = false,
  family = null,
  showMine = true,
  showFamily = true,
  reloadKey = 0,
  names = {},
  meId = null,
  onEditAuthored,
  header,
  footer,
}: CalendarProps) {
  const [load, setLoad] = useState<LoadState>({ phase: 'loading' });
  const familyTenantId = family?.tenantId ?? null;

  useEffect(() => {
    let cancelled = false;
    const from = new Date();
    from.setHours(0, 0, 0, 0);
    const to = new Date(from.getTime() + WINDOW_DAYS * 86_400_000);
    const range = { from: from.toISOString(), to: to.toISOString(), includeCancelled: true };
    // The family read is its own promise: its failure is named on the page
    // and never takes the cockpit's own agenda down with it.
    const familyRead: Promise<{ events: CalendarEventApi[] } | { error: string } | null> = familyTenantId
      ? listAgenda(familyTenantId, range).then(
          ({ events }) => ({ events }),
          (err: unknown) => ({ error: err instanceof ApiError ? err.message : 'the family calendar could not be read' })
        )
      : Promise.resolve(null);
    Promise.all([listAgenda(tenantId, range), familyRead])
      .then(([mine, fam]) => {
        if (cancelled) return;
        const events: AgendaEvent[] = mine.events.map((e) => ({ ...e, origin: 'mine' as const }));
        let familyError: string | null = null;
        if (fam && 'events' in fam) events.push(...fam.events.map((e) => ({ ...e, origin: 'family' as const })));
        if (fam && 'error' in fam) familyError = fam.error;
        events.sort((a, b) => a.startsAt.localeCompare(b.startsAt));
        setLoad({ phase: 'ready', events, holds: mine.holds, familyError });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoad({
          phase: 'error',
          message: err instanceof ApiError ? err.message : 'the calendar could not be read',
        });
      });
    return () => { cancelled = true; };
  }, [tenantId, familyTenantId, reloadKey]);

  const eyebrow = embedded ? null : <div class="calendar__eyebrow">Calendar</div>;

  if (load.phase === 'loading') {
    return (
      <div class="calendar">
        {eyebrow}
        {header}
        <div class="calendar__skeleton" aria-hidden="true">
          <div class="calendar__skeleton-row" />
          <div class="calendar__skeleton-row" />
        </div>
      </div>
    );
  }

  if (load.phase === 'error') {
    return (
      <div class="calendar">
        {eyebrow}
        {header}
        <div class="calendar__say">
          The calendar could not be read.
          <br />
          <span class="calendar__say-soft">{load.message}</span>
        </div>
      </div>
    );
  }

  // ⚑ B18g — HOLDS COUNT AS SOMETHING. The guard used to read
  // `events.length === 0`, which meant a fortnight held for a client showed
  // an empty calendar saying "nothing scheduled" — the promise invisible on
  // the one screen built to show what the time is spoken for. A day with only
  // a hold on it still has something to say.
  // The toggles decide what is SHOWN, never what was read: flipping one is
  // instant and costs no round trip.
  const hasFamily = family !== null;
  const shownEvents = load.events.filter((e) =>
    e.origin === 'mine' ? showMine || !hasFamily : showFamily
  );
  const shownHolds = showMine || !hasFamily ? load.holds : [];
  const familyLine = load.familyError && showFamily ? (
    <div class="calendar__family-error" role="status">
      The family calendar could not be read — {load.familyError}
    </div>
  ) : null;

  if (hasFamily && !showMine && !showFamily) {
    return (
      <div class="calendar">
        {eyebrow}
        {header}
        <div class="calendar__say">Mine and Family are both hidden.</div>
        {footer}
      </div>
    );
  }

  if (shownEvents.length === 0 && shownHolds.length === 0) {
    return (
      <div class="calendar">
        {eyebrow}
        {header}
        {familyLine}
        <div class="calendar__say">
          Nothing scheduled in the next {WINDOW_DAYS} days.
          <br />
          <span class="calendar__say-soft">
            An emailed invite becomes an event here as mail arrives — accepted,
            updated, or cancelled, the calendar follows the mail.
          </span>
        </div>
        {footer}
      </div>
    );
  }

  /** The small line under a title: whose, who added it, how it repeats. */
  function metaOf(e: AgendaEvent): string | null {
    const bits: string[] = [];
    if (hasFamily) bits.push(e.origin === 'family' ? 'Family' : 'Mine');
    if (e.visibility === 'private' && e.source === 'authored') bits.push('only you');
    if (e.source === 'authored' && e.createdByUserId && e.createdByUserId !== meId) {
      const who = names[e.createdByUserId];
      if (who) bits.push(`added by ${who}`);
    }
    const repeats = repeatWord(e.recurrenceRule);
    if (repeats) bits.push(repeats);
    return bits.length ? bits.join(' · ') : null;
  }

  function tenantOf(e: AgendaEvent): string {
    return e.origin === 'family' && family ? family.tenantId : tenantId;
  }

  const byDay = new Map<string, AgendaEvent[]>();
  for (const e of shownEvents) {
    const key = dayKey(e.startsAt);
    if (!byDay.has(key)) byDay.set(key, []);
    byDay.get(key)!.push(e);
  }

  // ⚑ B18g — HOLDS ARE NOT EVENTS AND MUST NEVER READ AS ONE. A hold is time
  // set aside for a promise, and showing it in the day's list would put a
  // meeting on Huan's calendar that nobody agreed to attend. So: its own
  // band, above the day's things, with its own word — and no click-through,
  // because there is nothing to open that is not the promise itself.
  //
  // ⚑ A SPAN IS NAMED ONCE AND CONTINUED THEREAFTER. The first version put a
  // full row on every covered day, and the walk's screenshot showed what that
  // actually looks like: three promises over a fortnight became forty-two
  // identical rows and the agenda was unreadable. Repeating a label does not
  // tell you anything the first one did not.
  //
  // But a middle day must still LOOK spoken for — a calendar that shows a
  // fortnight's promise only on its first morning is a calendar that says the
  // other thirteen days are free. So: the full row where a hold STARTS, and
  // one quiet continuation line per day after, carrying the COUNT (which is
  // the load — the thing the whole feature exists to show).
  const startsOn = new Map<string, typeof load.holds>();
  const continues = new Map<string, number>();
  for (const h of shownHolds) {
    for (let d = new Date(`${h.startsOn}T12:00:00`);
         d <= new Date(`${h.endsOn}T12:00:00`);
         d.setDate(d.getDate() + 1)) {
      const key = dayKey(d.toISOString());
      const isFirst = key === dayKey(new Date(`${h.startsOn}T12:00:00`).toISOString());
      if (isFirst) {
        if (!startsOn.has(key)) startsOn.set(key, []);
        startsOn.get(key)!.push(h);
      } else {
        continues.set(key, (continues.get(key) ?? 0) + 1);
      }
    }
  }

  // Every day that has EITHER, in order — a day with only a hold on it still
  // has something to say.
  const days = [...new Set([...byDay.keys(), ...startsOn.keys(), ...continues.keys()])].sort();

  return (
    <div class="calendar">
      {eyebrow}
      {header}
      {familyLine}
      {days.map((day) => (
        <section class="calendar__day" key={day}>
          <h2 class="calendar__day-title">{dayLabel(day)}</h2>
          {(startsOn.get(day) ?? []).map((h) => (
            <div class="calendar__hold" key={h.id}>
              <span class="calendar__hold-word">held</span>
              <span class="calendar__hold-label">
                {h.label}
                {h.endsOn !== h.startsOn ? (
                  <span class="calendar__hold-through"> through {throughLabel(h.endsOn)}</span>
                ) : null}
              </span>
            </div>
          ))}
          {continues.get(day) ? (
            <div class="calendar__hold is-continuing">
              <span class="calendar__hold-word">held</span>
              <span class="calendar__hold-label">
                {continues.get(day)} {continues.get(day) === 1 ? 'promise' : 'promises'} still running
              </span>
            </div>
          ) : null}
          {(byDay.get(day) ?? []).map((e) => {
            const meta = metaOf(e);
            const cls = `calendar__row${e.status === 'cancelled' ? ' is-cancelled' : ''}${e.origin === 'family' ? ' is-family' : ''}`;
            const body = (
              <>
                <span class="calendar__row-time">{e.allDay ? 'All day' : timeLabel(e)}</span>
                <span class="calendar__row-main">
                  <span class="calendar__row-title">
                    {e.status === 'cancelled' ? <span class="calendar__cancelled-word">cancelled · </span> : null}
                    {e.title ?? '(untitled)'}
                  </span>
                  {meta ? <span class="calendar__row-meta">{meta}</span> : null}
                  {e.location ? <span class="calendar__row-where">{e.location}</span> : null}
                </span>
              </>
            );
            // An authored event opens its edit sheet where the parent offers
            // one; an invite opens its preparation — on THIS tenant only,
            // because that spread reads the cockpit's tenant. A family invite
            // is read-only here, and says nothing it cannot do.
            if (onEditAuthored && e.source === 'authored' && e.status !== 'cancelled') {
              return (
                <button type="button" class={cls} key={e.id} onClick={() => onEditAuthored(e, tenantOf(e))}>
                  {body}
                </button>
              );
            }
            if (e.origin === 'mine') {
              return (
                <button type="button" class={cls} key={e.id} onClick={() => onOpen(e.id)}>
                  {body}
                </button>
              );
            }
            return (
              <div class={`${cls} is-static`} key={e.id}>
                {body}
              </div>
            );
          })}
        </section>
      ))}
      {footer}
    </div>
  );
}
